import { generateResultsPath } from '@AppRoutes/utils';
import { SearchTypes } from '@Constants/CommonConstants';
import UserDatabases from '@Services/UserDatabases';
import Breadcrumb from '@Services/Breadcrumb';
import searchStorage from '@Services/SearchStorage';
import { runSearch } from '@Pages/RetroSynthesis/ProjectRoute/ReaxysExamplesSearchService';
import { buildRequestPayloadForExamplesSearch } from '@Pages/RetroSynthesis/ProjectRoute/utils/conditionsDataBuilder';
import { CONDITIONS_RESULTS_BREADCRUMB_STATE } from './constants';

async function executeReactionIdsSearch(reactionIds) {
  const databaseId = UserDatabases.getDefaultDatabase()?.id;

  if (!databaseId) {
    throw new Error('Default Reaxys database is not available.');
  }

  const searchPayload = buildRequestPayloadForExamplesSearch({
    reactionIds,
    databaseId,
  });
  const { promise, cancel } = runSearch(searchPayload);

  try {
    const result = await promise;
    const searchId = await searchStorage.setNew({
      queryFactItem: searchPayload.queryFactItem,
      searchType: SearchTypes.REACTIONS_SEARCH,
    });
    const stateParams = {
      context: result?.context || searchPayload.context,
      databaseId: result?.databaseId || databaseId,
      resultName: result?.resultName,
      hitSetKey: result?.hitSetKey,
      pageNumber: 1,
      sort: '',
      limitOrExclude: '',
      searchId,
      resetBreadcrumbs: true,
      ...CONDITIONS_RESULTS_BREADCRUMB_STATE,
    };

    if (!stateParams.resultName || !stateParams.hitSetKey) {
      throw new Error('Reaxys reaction search did not return enough data to open results.');
    }

    Breadcrumb.resetBreadcrumbs();
    await Breadcrumb.createBreadcrumb(stateParams, result?.resultSize || result?.resultsCount);

    return {
      pathName: generateResultsPath(stateParams),
      stateParams,
    };
  } catch (error) {
    cancel?.();
    throw error;
  }
}

// Primary path: a normalized project carries the reaction RX.ID directly (parsed
// from the `reference` JSON), so no regex on the human-readable label is needed.
export async function searchReactionResultsByReactionId(reactionId) {
  if (!reactionId) {
    throw new Error('A Reaxys reaction RX.ID is required to open reaction results.');
  }

  const id = String(reactionId);
  const { pathName, stateParams } = await executeReactionIdsSearch([id]);

  return { reactionId: id, pathName, stateParams };
}

// Multi-ID search for the "N Examples" action — no client-side truncation (FR-3.6).
export async function searchReactionResultsByIds(reactionIds) {
  if (!Array.isArray(reactionIds) || reactionIds.length === 0) {
    throw new Error('At least one reaction ID is required to open reaction results.');
  }

  return executeReactionIdsSearch(reactionIds.map(String));
}
